import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { Loader2, LogOut, Server, Settings2 } from 'lucide-react';
import { useAuth } from './hooks/useAuth';
import { useAgentVersion } from './hooks/useAgentVersion';
import { SessionsPage } from './pages/SessionsPage';
import { SessionDetailPage } from './pages/SessionDetailPage';
import { RuntimeConfigPage } from './pages/RuntimeConfigPage';

function AppShell({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { version } = useAgentVersion();
  return (
    <div className="h-full flex flex-col">
      <nav className="border-b border-border bg-bg-card px-4 py-2 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-medium">
          <Server size={16} className="text-blue-600" />
          <span>Studio Agent · Debug</span>
          {version && <span className="text-xs text-text-muted ml-2 font-normal">v{version}</span>}
        </Link>
        {user && (
          <div className="flex items-center gap-3 text-xs text-text-muted">
            <Link
              to="/config"
              className="hover:text-text inline-flex items-center gap-1"
              title="Ver configuração vigente do agent"
            >
              <Settings2 size={12} /> config
            </Link>
            <a
              href="https://studio.hub-platform.com/admin/providers"
              className="hover:text-text inline-flex items-center gap-1"
              title="Configurar providers no Studio"
            >
              configurar no Studio →
            </a>
            <span>{user.email}</span>
            <a
              href="https://studio.hub-platform.com/logout"
              className="hover:text-text inline-flex items-center gap-1"
            >
              <LogOut size={12} /> sair
            </a>
          </div>
        )}
      </nav>
      <main className="flex-1 min-h-0 overflow-hidden">{children}</main>
    </div>
  );
}

function AuthGate({ children }: { children: React.ReactNode }) {
  const { user, loading, error, redirectToLogin } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-text-muted">
        <Loader2 size={20} className="animate-spin mr-2" />
        Verificando autenticação…
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="card max-w-md text-center">
          <h2 className="font-medium mb-2">Não autenticado</h2>
          <p className="text-sm text-text-muted mb-4">
            Você precisa estar logado em studio.hub-platform.com para acessar o painel
            de debug.
          </p>
          {error && error !== 'unauthenticated' && (
            <p className="text-xs text-red-600 mb-3">{error}</p>
          )}
          <button type="button" onClick={redirectToLogin} className="btn btn-primary">
            Ir para login
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <BrowserRouter>
      <AppShell>
        <AuthGate>
          <Routes>
            <Route path="/" element={<SessionsPage />} />
            <Route path="/sessions/:id" element={<SessionDetailPage />} />
            <Route path="/config" element={<RuntimeConfigPage />} />
            <Route
              path="*"
              element={
                <div className="text-center py-12 text-text-muted">
                  Página não encontrada.{' '}
                  <Link to="/" className="text-blue-600 underline">
                    voltar
                  </Link>
                </div>
              }
            />
          </Routes>
        </AuthGate>
      </AppShell>
    </BrowserRouter>
  );
}
